<template>
  <div>
    <HorizontalMenu
      :items="finalItems"
      size="large"
      :bottomLine="true"
      @onChange="handleTabChange"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import HorizontalMenu from "@components/HorizontalMenu/HorizontalMenu.vue";
import { LD_FLAG_IDEA_MDP_176 } from "@components/constants/launchDarklyFlagKeys";

const props = defineProps({
  yourEmployeesToCoachAndInspectCount: {
    type: Number,
    default: 0,
  },
  pendingInspectionRequestsCount: {
    type: Number,
    default: 0,
  },
  // Storybook/test override so the new tabs can be previewed without a
  // live LaunchDarkly context (same pattern as AsACoachPage's forceMDP193).
  showInspectionRequestsTabs: {
    type: Boolean,
    default: undefined,
  },
  items: {
    type: Array,
    default: null,
  },
});

const emit = defineEmits(["tab-change"]);

const { proxy } = getCurrentInstance() || {};
const flagEnabled = ref(true);

onMounted(async () => {
  try {
    // idea-MDP-176 is a kill switch for the coach inspection-requests tabs:
    // they render by default and the flag only hides them when it explicitly
    // resolves false (fail-open on error, matching the shipped coach surface).
    flagEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_176, true)) !== false;
  } catch (error) {
    flagEnabled.value = true;
  }
});

const inspectionRequestsTabsEnabled = computed(() => {
  if (props.showInspectionRequestsTabs !== undefined) {
    return props.showInspectionRequestsTabs;
  }
  return flagEnabled.value;
});

const employeesTabItem = computed(() => ({
  title: "Your Employees to Inspect",
  count: props.yourEmployeesToCoachAndInspectCount,
}));

const inspectionRequestTabItems = computed(() => [
  { title: "Pending", count: props.pendingInspectionRequestsCount },
  { title: "Accepted", count: null },
  { title: "History of Inspection Requests", count: null },
]);

const defaultItems = computed(() =>
  inspectionRequestsTabsEnabled.value
    ? [employeesTabItem.value, ...inspectionRequestTabItems.value]
    : [employeesTabItem.value]
);

const finalItems = computed(() => props.items ?? defaultItems.value);

function handleTabChange(index) {
  const item = finalItems.value[index];
  emit("tab-change", item?.title, index);
}
</script>

<style scoped>
:deep(.v-tab) {
  max-width: 400px !important;
  color: #475467 !important;
}

:deep(.horizontal-tab .menu-tabs.v-tab.v-tab--active) {
  color: #7f56d9 !important;
}

:deep(.horizontal-large-tab .menu-tabs.v-tab.v-tab--active .count-badge .v-badge__badge) {
  border-radius: 150px !important;
  border-color: #f2edff !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  color: #7f56d9;
  height: 25px;
  min-width: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.horizontal-tab .v-tabs-slider-wrapper) {
  color: #d6bbfb !important;
}

:deep(.v-divider) {
  margin: 12px 0px !important;
}
</style>
